import { hashKey, useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  clearActiveRemovedNoticeToast,
  clearRemovedNotice,
  getRemovedNotice,
  hasActiveRemovedNoticeToast,
  setActiveRemovedNoticeToast,
  subscribeRemovedNotice,
  subscribeRemovedNoticeRetraction,
} from '@/common/queries/itemsTreeSetRemovedNotice';
import { useItemFetchQuery } from '@/common/queries/useGetItemFetchQuery';

// Shows a generic toast immediately, then upgrades it in place with the item name and an "Open"
// action once a background fetch confirms it's actually openable — action-less on failure or batch.
export const useTreeSetFilteredOutToast = ({
  queryKey,
  enabled,
  openItemPanel,
}: {
  queryKey: readonly unknown[];
  enabled: boolean;
  openItemPanel: (params: { itemId: number }) => void;
}): void => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKeyString = React.useMemo(() => hashKey(queryKey), [queryKey]);

  const subscribe = React.useCallback(
    (listener: () => void) =>
      enabled ? subscribeRemovedNotice(queryKeyString, listener) : () => {},
    [enabled, queryKeyString],
  );
  const getSnapshot = React.useCallback(
    () => (enabled ? getRemovedNotice(queryKeyString) : undefined),
    [enabled, queryKeyString],
  );
  const notice = React.useSyncExternalStore(subscribe, getSnapshot);

  const toastIdRef = React.useRef<string | number | null>(null);
  // Sonner un-dismisses a toast id the moment it's reused, so the upgrade below must check this.
  const dismissedRef = React.useRef(false);
  // Bumped per toast created, so a stale onDismiss or fetch-effect closure can tell it's superseded.
  const toastGenerationRef = React.useRef(0);
  // The single item this toast represents, checked against a delete's retraction.
  const activeItemIdRef = React.useRef<number | undefined>(undefined);

  // The id being confirmed as openable, paired with its toast's generation — state, not a ref, since
  // a newer effect run would bump a ref before a stale one gets to check it.
  const [pending, setPending] = React.useState<{ itemId: number; generation: number } | undefined>(
    undefined,
  );

  React.useEffect(() => {
    if (!notice) return;
    // A delete's retraction can invalidate this notice between render and this effect running.
    if (getRemovedNotice(queryKeyString) !== notice) return;
    clearRemovedNotice(queryKeyString);

    const removedIds = notice.diff.removedIds;
    if (removedIds.length === 0) return;

    if (toastIdRef.current != null) {
      toast.dismiss(toastIdRef.current);
      if (activeItemIdRef.current !== undefined) {
        clearActiveRemovedNoticeToast(queryClient, activeItemIdRef.current, toastIdRef.current);
      }
    }
    dismissedRef.current = false;
    const generation = ++toastGenerationRef.current;
    const singleItemId = removedIds.length === 1 ? (removedIds[0] as number) : undefined;
    const onDismiss = () => {
      if (toastGenerationRef.current !== generation) return;
      dismissedRef.current = true;
      if (singleItemId !== undefined && toastIdRef.current != null) {
        clearActiveRemovedNoticeToast(queryClient, singleItemId, toastIdRef.current);
      }
    };

    if (removedIds.length > 1) {
      setPending(undefined);
      activeItemIdRef.current = undefined;
      toastIdRef.current = toast.info(
        t(($) => $.toast.itemsFilteredOut.title, { count: removedIds.length }),
        {
          description: t(($) => $.toast.itemsFilteredOut.description, { count: removedIds.length }),
          onDismiss,
        },
      );
      return;
    }

    toastIdRef.current = toast.info(
      t(($) => $.toast.itemsFilteredOut.title, { count: 1 }),
      {
        description: t(($) => $.toast.itemsFilteredOut.description, { count: 1 }),
        onDismiss,
      },
    );
    activeItemIdRef.current = singleItemId;
    setActiveRemovedNoticeToast(queryClient, singleItemId as number, toastIdRef.current);
    setPending({ itemId: singleItemId as number, generation });
  }, [notice, queryKeyString, queryClient, t]);

  React.useEffect(() => {
    if (!enabled) return;
    // Dismissal is handled centrally by retractRemovedNoticeToast — this just clears local state.
    return subscribeRemovedNoticeRetraction(queryClient, (itemId) => {
      if (activeItemIdRef.current !== itemId) return;
      activeItemIdRef.current = undefined;
      setPending(undefined);
    });
  }, [queryClient, enabled]);

  const itemQuery = useItemFetchQuery({
    itemId: pending?.itemId ?? 0,
    enabled: enabled && pending !== undefined,
  });

  React.useEffect(() => {
    if (pending === undefined || toastIdRef.current == null) return;
    // A newer toast now owns toastIdRef.current — this closure's fetch is for a superseded one.
    if (pending.generation !== toastGenerationRef.current) return;
    if (activeItemIdRef.current !== pending.itemId) return;
    // A retraction while disabled has no local listener, but removes the toast centrally.
    if (!hasActiveRemovedNoticeToast(queryClient, pending.itemId, toastIdRef.current)) {
      activeItemIdRef.current = undefined;
      setPending(undefined);
      return;
    }
    // Disabled mid-fetch — leave the toast as-is, un-upgraded.
    if (!enabled) return;
    // isFetching also catches a stale cached hit still being confirmed by a background refetch.
    if (itemQuery.isPending || itemQuery.isFetching) return;

    // Failed, empty, or already dismissed — leave it alone rather than resurrect it with new content.
    if (itemQuery.isError || !itemQuery.data || dismissedRef.current) {
      setPending(undefined);
      return;
    }

    const item = itemQuery.data;
    const itemId = pending.itemId;
    toast.info(
      t(($) => $.toast.itemsFilteredOut.title, { count: 1 }),
      {
        id: toastIdRef.current,
        description: t(($) => $.toast.itemsFilteredOut.descriptionNamed, { name: item.name }),
        action: {
          label: t(($) => $.toast.itemsFilteredOut.action),
          onClick: () => openItemPanel({ itemId }),
        },
      },
    );
    setPending(undefined);
  }, [
    pending,
    enabled,
    itemQuery.isPending,
    itemQuery.isFetching,
    itemQuery.isError,
    itemQuery.data,
    queryClient,
    t,
    openItemPanel,
  ]);
};
